import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { FieldDefinition, MapDefinition } from '@grs-atlas/shared';
import { isAdmin, MapDefinitionSchema } from '@grs-atlas/shared';
import { useAuthRoles } from '../../auth/AuthContext';
import { Toast } from '../../components/Toast';
import { useCreateMap } from '../../services/hooks';
import { MapFieldsEditor } from './MapFieldsEditor';
import {
  DEFAULT_COLOR_RULE,
  deriveListFields,
  slugifyMapId,
  starterFields,
} from './mapDefinitionDefaults';

export function AdminCreateMapPage() {
  const roles = useAuthRoles();
  const navigate = useNavigate();
  const createMap = useCreateMap();
  const [mapIdTouched, setMapIdTouched] = useState(false);
  const [name, setName] = useState('');
  const [mapId, setMapId] = useState('');
  const [description, setDescription] = useState('');
  const [recordLabel, setRecordLabel] = useState('Record');
  const [fields, setFields] = useState<FieldDefinition[]>(() => starterFields());
  const [toast, setToast] = useState<{ message: string; type?: 'error' | 'success' } | null>(
    null,
  );

  if (!isAdmin(roles)) {
    return <div className="empty">Administrator role required.</div>;
  }

  const onNameChange = (value: string) => {
    setName(value);
    if (!mapIdTouched) setMapId(slugifyMapId(value));
  };

  const submit = async () => {
    const lists = deriveListFields(fields);
    const draft: MapDefinition = {
      mapId,
      name,
      description,
      recordLabel: recordLabel || 'Record',
      version: 1,
      status: 'active',
      fields,
      ...lists,
      colorRule: DEFAULT_COLOR_RULE,
    };

    const parsed = MapDefinitionSchema.safeParse(draft);
    if (!parsed.success) {
      const first = parsed.error.issues[0];
      setToast({
        message: first
          ? `${first.path.join('.') || 'definition'}: ${first.message}`
          : 'Invalid map definition.',
        type: 'error',
      });
      return;
    }

    if (!fields.some((f) => f.type === 'states')) {
      setToast({
        message: 'Add at least one field with type “states” so records can paint on the map.',
        type: 'error',
      });
      return;
    }

    try {
      const created = await createMap.mutateAsync(parsed.data);
      setToast({ message: 'Map created.', type: 'success' });
      navigate(`/admin/maps/${created.mapId}`);
    } catch (err) {
      setToast({
        message: err instanceof Error ? err.message : 'Create failed',
        type: 'error',
      });
    }
  };

  return (
    <div style={{ padding: '1.5rem', maxWidth: 960, margin: '0 auto' }}>
      <p>
        <Link to="/admin/maps">← Admin maps</Link>
      </p>
      <h1>Create map type</h1>
      <p style={{ color: 'var(--muted)', marginTop: 0 }}>
        Define a new map. Start with the defaults, then add the fields your records need.
      </p>

      <div className="form-grid">
        <label>
          Name
          <input
            value={name}
            placeholder="e.g. Partner Pipeline"
            onChange={(e) => onNameChange(e.target.value)}
          />
        </label>
        <label>
          Map ID
          <input
            value={mapId}
            placeholder="e.g. partner-pipeline"
            onChange={(e) => {
              setMapIdTouched(true);
              setMapId(
                e.target.value
                  .toLowerCase()
                  .replace(/[^a-z0-9-]/g, '')
                  .slice(0, 64),
              );
            }}
          />
          <span style={{ fontSize: '0.75rem', color: 'var(--muted)', fontWeight: 400 }}>
            Lowercase letters, numbers, and hyphens. Cannot be changed later.
          </span>
        </label>
        <label>
          Description
          <textarea
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>
        <label>
          Record label
          <input
            value={recordLabel}
            placeholder="e.g. Partner"
            onChange={(e) => setRecordLabel(e.target.value)}
          />
        </label>

        <h2 style={{ marginBottom: 0 }}>Fields</h2>
        <MapFieldsEditor
          fields={fields}
          lockedKeys={new Set(['name', 'stateCodes'])}
          onChange={setFields}
        />

        <div className="form-actions">
          <Link className="btn secondary" to="/admin/maps">
            Cancel
          </Link>
          <button
            type="button"
            className="btn"
            disabled={createMap.isPending || !name.trim() || !mapId.trim()}
            onClick={() => void submit()}
          >
            {createMap.isPending ? 'Creating…' : 'Create map'}
          </button>
        </div>
      </div>

      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </div>
  );
}
